import type { PricingTable } from '@hotjar/webapp/domains/revenue/pricing/types';
import { useEffect, useState } from 'react';
import { subscriptionAPI } from '@hotjar/webapp/billing/subscription';
import { useOwnedAccount } from '@hotjar/account';
import { getNewSiteCosts, prettifyPriceWithCurrencySymbol } from '@hotjar/webapp/billing/cost';
import { useSettings } from '@hotjar/webapp/hooks/useSettings';

const prettifyCost = (cost?: number | string, symbol?: string) =>
  cost ? prettifyPriceWithCurrencySymbol(symbol)(cost) : '';

export const useProRataCost = (
  accountPricingTable: PricingTable,
  selectedPlanId: string,
  selectedOrgId: string,
): {
  proRataCost?: string;
  totalRecurringCost?: string;
  newSiteRecurringCost?: string;
  billingCycleTerm: string;
} => {
  const [proRataCost, setProRataCost] = useState<string>();
  const [totalRecurringCost, setTotalRecurringCost] = useState<string>();
  const [newSiteRecurringCost, setNewSiteRecurringCost] = useState<string>();

  const { data: ownedAccount } = useOwnedAccount();
  const { data: settings } = useSettings();

  const currency = ownedAccount?.currency;

  useEffect(() => {
    const symbol = currency ? settings?.currency_symbols?.[currency] : undefined;

    if (accountPricingTable[selectedPlanId]?.eq(0)) {
      setProRataCost(prettifyCost(0, symbol));
    } else if (ownedAccount?.id) {
      subscriptionAPI.getProRata(ownedAccount.id, selectedPlanId, selectedOrgId).then((data) => {
        if (data.success) {
          setProRataCost(prettifyCost(data.totalCost, symbol));
        }
      });

      getNewSiteCosts(ownedAccount, selectedPlanId).then(
        ({ totalRecurringCost: totalRecurring, newSiteRecurringCost: newSiteRecurring }) => {
          setTotalRecurringCost(prettifyCost(totalRecurring, symbol));
          setNewSiteRecurringCost(prettifyCost(newSiteRecurring, symbol));
        },
      );
    }
  }, [
    accountPricingTable,
    currency,
    ownedAccount,
    selectedOrgId,
    selectedPlanId,
    settings?.currency_symbols,
  ]);

  const billingCycleTerm = ownedAccount?.billingCycle === 'yearly_recurring' ? 'year' : 'month';

  return {
    proRataCost,
    totalRecurringCost,
    newSiteRecurringCost,
    billingCycleTerm,
  };
};
